<template>
  <div class="setup-step">
    <h3>
      {{
        sprintf(s__('ProjectSetup|Step %{step}: Group Chat Notifications'), { step: stepNumber })
      }}
    </h3>
    <p class="step-description">
      {{
        s__(
          'ProjectSetup|Configure group chat notifications for this project. When enabled, task updates will be posted to the project group chat.'
        )
      }}
    </p>

    <div v-if="!isPersonalProject" class="form-row">
      <label class="checkbox-label">
        <input v-model="groupChatNotificationsEnabled" type="checkbox" :disabled="saving" />
        <span>{{ s__('ProjectSetup|Enable group chat notifications') }}</span>
      </label>
      <div class="help-text">
        {{ s__('ProjectSetup|Send task updates to the project group chat') }}
      </div>
    </div>

    <div v-else class="info-box">
      <p>
        {{ s__('ProjectSetup|Group chat notifications are not available for personal projects.') }}
      </p>
    </div>

    <div
      v-if="!isPersonalProject && groupChatNotificationsEnabled"
      class="notification-events-section"
    >
      <h4 class="section-title">{{ s__('ProjectSetup|Notification Events') }}</h4>
      <div class="help-text">
        {{
          s__(
            'ProjectSetup|Control which events trigger group chat notifications. Note: all events will still send direct messages.'
          )
        }}
      </div>
      <div class="mobile-underlined-checklist">
        <div
          v-for="eventType in commonEventTypes"
          :key="eventType.value"
          class="notification-preference-row mobile-underlined-checklist-item"
        >
          <label class="checkbox-label">
            <input
              type="checkbox"
              :checked="isEventEnabled(eventType.value)"
              :disabled="saving"
              @change="toggleEvent(eventType.value, $event.target.checked)"
            />
            <span>{{ eventType.label }}</span>
          </label>
        </div>
      </div>
      <div v-if="!showAdvancedEvents" class="show-more-container">
        <button
          type="button"
          class="show-more-btn"
          :disabled="saving"
          @click="showAdvancedEvents = true"
        >
          {{ s__('ProjectSetup|Show more events') }}
        </button>
      </div>
      <div v-if="showAdvancedEvents">
        <div class="mobile-underlined-checklist">
          <div
            v-for="eventType in advancedEventTypes"
            :key="eventType.value"
            class="notification-preference-row mobile-underlined-checklist-item"
          >
            <label class="checkbox-label">
              <input
                type="checkbox"
                :checked="isEventEnabled(eventType.value)"
                :disabled="saving"
                @change="toggleEvent(eventType.value, $event.target.checked)"
              />
              <span>{{ eventType.label }}</span>
            </label>
          </div>
        </div>
        <div class="show-more-container">
          <button
            type="button"
            class="show-more-btn"
            :disabled="saving"
            @click="showAdvancedEvents = false"
          >
            {{ s__('ProjectSetup|Show fewer events') }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="saveError" class="error">{{ saveError }}</div>
    <div v-if="saveSuccess" class="success">
      {{ s__('ProjectSetup|Preferences saved successfully!') }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, sprintf } from '@/locale'
import '../../../styles/mobile-underlined-checklist.css'

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
  stepNumber: {
    type: Number,
    required: true,
  },
  saving: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['save', 'skip'])

const groupChatNotificationsEnabled = ref(true)
const originalGroupChatNotificationsEnabled = ref(true)
const notificationPreferences = ref({})
const originalPreferences = ref({})
const pendingChanges = ref({})
const saveError = ref('')
const saveSuccess = ref(false)

const isPersonalProject = computed(() => props.project?.isPersonal || false)

// Common event types shown by default
const commonEventTypes = [
  { value: 'created', label: s__('ProjectSetup|Task created') },
  { value: 'status_changed', label: s__('ProjectSetup|Status changed') },
  { value: 'completed', label: s__('ProjectSetup|Task completed') },
  { value: 'task_priority_updated', label: s__('ProjectSetup|Priority updated') },
  { value: 'task_due_at_updated', label: s__('ProjectSetup|Due date updated') },
  { value: 'task_assignee_id_updated', label: s__('ProjectSetup|Assignee changed') },
  { value: 'comment_added', label: s__('ProjectSetup|Comment added') },
]

// Advanced event types shown when "Show more" is clicked
const advancedEventTypes = [
  { value: 'label_attached', label: s__('ProjectSetup|Label attached') },
  { value: 'label_detached', label: s__('ProjectSetup|Label detached') },
  { value: 'task_title_updated', label: s__('ProjectSetup|Title updated') },
  {
    value: 'task_description_updated',
    label: s__('ProjectSetup|Description updated'),
  },
  {
    value: 'task_confidential_updated',
    label: s__('ProjectSetup|Confidentiality changed'),
  },
  {
    value: 'task_archived_updated',
    label: s__('ProjectSetup|Archive status changed'),
  },
  {
    value: 'horizontal_relation_created',
    label: s__('ProjectSetup|Task relation created'),
  },
  {
    value: 'horizontal_relation_updated',
    label: s__('ProjectSetup|Task relation updated'),
  },
  {
    value: 'horizontal_relation_destroyed',
    label: s__('ProjectSetup|Task relation removed'),
  },
  {
    value: 'vertical_relation_created',
    label: s__('ProjectSetup|Task hierarchy created'),
  },
  {
    value: 'vertical_relation_destroyed',
    label: s__('ProjectSetup|Task hierarchy removed'),
  },
]

// All event types combined (for reference)
const allEventTypes = [...commonEventTypes, ...advancedEventTypes]

const showAdvancedEvents = ref(false)

// Computed property to check if any advanced event is enabled
const hasEnabledAdvancedEvents = computed(() => {
  const prefs = notificationPreferences.value || {}

  return advancedEventTypes.some(event => {
    if (!prefs.hasOwnProperty(event.value)) {
      return true
    }
    const value = prefs[event.value]
    return (
      value === true || value === 'true' || value === 1 || String(value).toLowerCase() === 'true'
    )
  })
})

// Watch the computed property to auto-expand when advanced events are enabled
watch(
  hasEnabledAdvancedEvents,
  hasEnabled => {
    showAdvancedEvents.value = hasEnabled
  },
  { immediate: true }
)

const UPDATE_PROJECT_MUTATION = gql`
  mutation UpdateProject($id: ID!, $groupChatNotificationsEnabled: Boolean) {
    updateProject(id: $id, groupChatNotificationsEnabled: $groupChatNotificationsEnabled) {
      project {
        id
        groupChatNotificationsEnabled
      }
      errors
    }
  }
`

const UPDATE_NOTIFICATION_PREFERENCES = gql`
  mutation UpdateProjectNotificationPreferences($id: ID!, $notificationPreferences: JSON!) {
    updateProjectNotificationPreferences(
      id: $id
      notificationPreferences: $notificationPreferences
    ) {
      project {
        id
        notificationPreferences
      }
      errors
    }
  }
`

const { mutate: updateProject } = useMutation(UPDATE_PROJECT_MUTATION)
const { mutate: updateNotificationPreferencesMutation } = useMutation(
  UPDATE_NOTIFICATION_PREFERENCES
)

// Initialize from project
watch(
  () => props.project,
  projectData => {
    if (projectData) {
      groupChatNotificationsEnabled.value = projectData.groupChatNotificationsEnabled ?? true
      originalGroupChatNotificationsEnabled.value =
        projectData.groupChatNotificationsEnabled ?? true
      notificationPreferences.value = projectData.notificationPreferences || {}
      originalPreferences.value = JSON.parse(JSON.stringify(notificationPreferences.value))
      pendingChanges.value = {}
    }
  },
  { immediate: true }
)

function isEventEnabled(eventType) {
  // If there's a pending change, use that
  if (pendingChanges.value.hasOwnProperty(eventType)) {
    return pendingChanges.value[eventType] === true
  }
  // Otherwise check the current preferences (defaults to true if not set)
  const preference = notificationPreferences.value[eventType]
  return preference !== false
}

function toggleEvent(eventType, enabled) {
  // If the new value matches the original, remove from pending changes
  const originalValue = originalPreferences.value[eventType]
  const wouldBeDefault = originalValue === undefined && enabled === true

  if (wouldBeDefault || originalValue === enabled) {
    // Remove from pending changes
    const { [eventType]: _, ...rest } = pendingChanges.value
    pendingChanges.value = rest
  } else {
    // Add to pending changes
    pendingChanges.value = {
      ...pendingChanges.value,
      [eventType]: enabled,
    }
  }
}

// Check if there are unsaved changes
function hasChanges() {
  return (
    groupChatNotificationsEnabled.value !== originalGroupChatNotificationsEnabled.value ||
    Object.keys(pendingChanges.value).length > 0
  )
}

// Reset changes
function reset() {
  groupChatNotificationsEnabled.value = originalGroupChatNotificationsEnabled.value
  notificationPreferences.value = JSON.parse(JSON.stringify(originalPreferences.value))
  pendingChanges.value = {}
  saveError.value = ''
  saveSuccess.value = false
}

// Expose methods to parent
defineExpose({
  hasChanges,
  reset,
  async save() {
    if (!props.project?.id) return

    saveError.value = ''
    saveSuccess.value = false

    try {
      // First update group chat notifications enabled flag
      if (groupChatNotificationsEnabled.value !== originalGroupChatNotificationsEnabled.value) {
        const { data: projectData } = await updateProject({
          id: props.project.id,
          groupChatNotificationsEnabled: groupChatNotificationsEnabled.value,
        })

        if (projectData?.updateProject?.errors?.length > 0) {
          throw new Error(projectData.updateProject.errors.join(', '))
        }

        originalGroupChatNotificationsEnabled.value = groupChatNotificationsEnabled.value
      }

      // Then update notification preferences if there are changes
      if (Object.keys(pendingChanges.value).length > 0) {
        // Build the preferences object to send
        // The service merges with existing preferences, so we send only the changes
        // For enabled events (true), we send null to remove the preference (defaults to enabled)
        // For disabled events (false), we send false to explicitly disable
        const preferencesToSave = {}

        Object.keys(pendingChanges.value).forEach(eventType => {
          const value = pendingChanges.value[eventType]
          if (value === true) {
            // Setting to true means removing the preference (default behavior)
            // Send null to clear it
            preferencesToSave[eventType] = null
          } else {
            // Setting to false means explicitly disabling
            preferencesToSave[eventType] = false
          }
        })

        const { data: preferencesData } = await updateNotificationPreferencesMutation({
          id: props.project.id,
          notificationPreferences: preferencesToSave,
        })

        if (preferencesData?.updateProjectNotificationPreferences?.errors?.length > 0) {
          throw new Error(preferencesData.updateProjectNotificationPreferences.errors.join(', '))
        }

        // Update local state
        if (preferencesData?.updateProjectNotificationPreferences?.project) {
          notificationPreferences.value =
            preferencesData.updateProjectNotificationPreferences.project.notificationPreferences ||
            {}
          originalPreferences.value = JSON.parse(JSON.stringify(notificationPreferences.value))
        }
        pendingChanges.value = {}
      }

      saveSuccess.value = true
      emit('save', {
        groupChatNotificationsEnabled: groupChatNotificationsEnabled.value,
        notificationPreferences: notificationPreferences.value,
      })
      return true
    } catch (e) {
      saveError.value = e.message || 'Failed to save preferences'
      return false
    }
  },
})
</script>

<style scoped>
.setup-step {
  max-width: 600px;
}

.setup-step h3 {
  margin-top: 0;
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.step-description {
  margin-bottom: 24px;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.form-row {
  margin-bottom: 16px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 500;
  color: #333;
}

.checkbox-label input[type='checkbox'] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.checkbox-label input[type='checkbox']:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.help-text {
  margin-top: 8px;
  margin-left: 26px;
  font-size: 13px;
  color: #666;
}

.info-box {
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
}

.notification-events-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.section-title {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

@media (min-width: 768px) {
  .notification-preference-row {
    padding: 8px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }

  .notification-preference-row:last-child {
    border-bottom: none;
  }
}

.error {
  color: #d32f2f;
  font-size: 14px;
  margin-top: 8px;
}

.success {
  color: #2e7d32;
  font-size: 14px;
  margin-top: 8px;
}

.show-more-container {
  margin-top: 12px;
  padding-top: 12px;
}
</style>
